import {NumberField} from '@wandb/components';
import React, {useCallback, useState} from 'react';

import {useDebouncedValue} from '../../../../../util/useDebouncedValue';
import {
  useMediaStepStrideLengthDisplay,
  useShouldShowMediaStepStrideLengthResetAtLevel,
} from '../../hooks';
import {SettingTitle} from '../common/SettingTitle';
import {useMediaSettingsContext} from '../contexts/MediaSettingsContextProvider';

const DEFAULT_STRIDE_LENGTH = 1;

export const StrideLength = () => {
  const {updateConfig, unsetConfigField, settingsLevel, panelRef} =
    useMediaSettingsContext();
  const stepStrideLength = useMediaStepStrideLengthDisplay(
    settingsLevel,
    panelRef
  );
  const isSetAtLevel = useShouldShowMediaStepStrideLengthResetAtLevel(
    settingsLevel,
    panelRef
  );
  const updateStrideLength = useCallback(
    (newValue?: number) => {
      const parsedValue = newValue ?? DEFAULT_STRIDE_LENGTH;
      if (parsedValue === DEFAULT_STRIDE_LENGTH) {
        // avoid storing default values in the config
        unsetConfigField('stepStrideLength');
      } else {
        updateConfig({
          stepStrideLength: parsedValue,
        });
      }
    },
    [updateConfig, unsetConfigField]
  );

  const {value: debouncedValue, debouncedSetValue} = useDebouncedValue(
    stepStrideLength,
    500,
    updateStrideLength
  );

  // Local state to control the value of the input so we don't have to wait for the updateConfig call to propagate
  const [value, setValue] = useState(debouncedValue);
  const onChange = useCallback(
    (newValue?: number) => {
      setValue(newValue);
      debouncedSetValue(newValue);
    },
    [debouncedSetValue]
  );

  return (
    <div
      className="flex items-center justify-between"
      data-test="step-stride-length-input">
      <SettingTitle
        title="Stride length"
        configField="stepStrideLength"
        isDirty={isSetAtLevel}
        unsetConfigField={() => unsetConfigField('stepStrideLength')}
      />
      <NumberField
        defaultValue={1}
        minValue={1}
        step={1}
        value={value}
        onChange={onChange}
      />
    </div>
  );
};
